// GroupsTab.jsx
//
// Верхній рівень "Групи в садочках": зведення по Києву та районах, з
// розгортанням до садочків (GroupsBreakdownTable), а всередині — до
// конкретних груп (GroupInlineDetail всередині GroupsBreakdownTable).
// Структура повністю аналогічна ApplicationsTab.jsx.
//
// disabilities більше не завантажуються тут — вони приходять пропсом
// з index.jsx, де завантажуються один раз для всього модуля.
import React, { useEffect, useState, useMemo } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import { useGroupsData } from './hooks/useStatisticsData';
import { useDragScroll } from './hooks/useDragScroll';
import { useIsMobile } from './hooks/useIsMobile';
import { resolveMultiParam, resolveBenefitsParam } from './statisticsApi';
import GroupsBreakdownTable from './GroupsBreakdownTable';
import MobileSheet from './MobileSheet';
import { externalTooltipHandler } from './utils/externalTooltip';
import './chartConfig';

const CITY_ALL_KEY = '__all_kyiv__';

const GroupsTab = ({ filters, onLoading, onError, disabilities = [] }) => {
  const { data, loading, error, fetchData } = useGroupsData();
  const [openDistrict, setOpenDistrict] = useState(null);

  const { ref: scrollRef, listeners: dragListeners } = useDragScroll();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (filters.year) {
      const yearNum = parseInt(filters.year.split('→')[0].trim());

      const ageArr = Array.isArray(filters.ageCategory) ? filters.ageCategory : ['all'];
      const isNf = ageArr.includes('with_nf') || ageArr.includes('only_nf');
      const onlyNf = ageArr.includes('only_nf');
      const specificAges = ageArr.filter(v => v !== 'all' && v !== 'with_nf' && v !== 'only_nf');

      const apiFilters = {
        year: yearNum,
        distr: resolveMultiParam(filters.district),
        age_type: specificAges.length ? specificAges.join(',') : 'all',
        place_type: resolveMultiParam(filters.placeType),
        disability_type: resolveMultiParam(filters.disabilityType),
        international_agreement: filters.internationalAgreement || 'all',
        benefits: resolveBenefitsParam(filters.benefit),
        include_nf: isNf,
        only_nf: onlyNf,
      };
      fetchData(apiFilters);
    }
  }, [filters, fetchData]);

  useEffect(() => { if (onLoading) onLoading(loading); }, [loading, onLoading]);
  useEffect(() => { if (onError && error) onError(error); }, [error, onError]);

  const kgRows = useMemo(() => {
    if (!data || data.length === 0) return [];

    const rows = [];
    data.forEach((item, index) => {
      let numbPlaces = 0, numbPlacesPlan = 0, numberChildrens = 0, numbRes = 0;
      let kyivCount = item.kyiv || 0;
      let groupsCount = 0;
      let difficulties = {};
      let ageCategories = {};

      item.groups.forEach(group => {
        if (group.type !== 15) groupsCount++;
        Object.values(group.numberSite).forEach(p => numbPlaces += p);
        if (group.planedSite && Object.keys(group.planedSite).length > 0)
          Object.values(group.planedSite).forEach(p => numbPlacesPlan += p);
        if (group.info && group.info.length > 0)
          group.info.forEach(ch => numberChildrens += ch.numb_ch);
        if (group.reserv && group.reserv.length > 0)
          numbRes += group.reserv.length;
        if (group.disabilitiesType) {
          const d = disabilities.find(x => x._id === group.disabilitiesType);
          const label = d ? (d.label || d.name) : group.disabilitiesType;
          difficulties[label] = (difficulties[label] || 0) + 1;
        }
        if (group.typeName)
          ageCategories[group.typeName] = (ageCategories[group.typeName] || 0) + 1;
      });

      rows.push({
        id: item.kgId ?? index + 1,
        kgId: item.kgId,
        zdo: item.number,
        district: item.district,
        groupsCount,
        totalPlaces: numbPlaces,
        plannedPlaces: numbPlacesPlan,
        children: numberChildrens,
        kyiv: kyivCount,
        reserve: numbRes,
        free: numbPlaces - numberChildrens - numbRes,
        difficulties: Object.keys(difficulties).join(', ') || 'без труднощів',
        ageCategories: Object.keys(ageCategories).join(', '),
        _item: item,
      });
    });
    return rows;
  }, [data, disabilities]);

  const kgRowsByDistrict = useMemo(() => {
    const groups = {};
    kgRows.forEach(row => {
      if (!groups[row.district]) groups[row.district] = [];
      groups[row.district].push(row);
    });
    return groups;
  }, [kgRows]);

  const allKgRows = kgRows;

  const districtRows = useMemo(() => {
    const groups = {};
    kgRows.forEach(r => {
      if (!groups[r.district]) {
        groups[r.district] = {
          district: r.district,
          groupsCount: 0, totalPlaces: 0, plannedPlaces: 0,
          children: 0, kyiv: 0, reserve: 0, free: 0,
        };
      }
      const g = groups[r.district];
      g.groupsCount   += r.groupsCount;
      g.totalPlaces   += r.totalPlaces;
      g.plannedPlaces += r.plannedPlaces;
      g.children      += r.children;
      g.kyiv          += r.kyiv;
      g.reserve       += r.reserve;
      g.free          += r.free;
    });
    return Object.values(groups).sort((a, b) => a.district.localeCompare(b.district, 'uk'));
  }, [kgRows]);

  const cityTotalRow = useMemo(() => {
    if (!districtRows.length) return null;
    const t = { district: 'По Києву', groupsCount: 0, totalPlaces: 0, plannedPlaces: 0, children: 0, kyiv: 0, reserve: 0, free: 0 };
    districtRows.forEach(r => {
      t.groupsCount += r.groupsCount; t.totalPlaces += r.totalPlaces; t.plannedPlaces += r.plannedPlaces;
      t.children += r.children; t.kyiv += r.kyiv; t.reserve += r.reserve; t.free += r.free;
    });
    return t;
  }, [districtRows]);

  const ageCategoryTotals = useMemo(() => {
    if (!data) return [];
    const totals = {};
    data.forEach(item => {
      item.groups.forEach(group => {
        if (group.type === 15) return;
        if (group.typeName) totals[group.typeName] = (totals[group.typeName] || 0) + 1;
      });
    });
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const AGE_CATEGORY_COLORS = [
    '#3498db', '#58d68d', '#f4d03f', '#e74c3c', '#9b59b6',
    '#1abc9c', '#e8a898', '#a9dfbf', '#f7dc6f', '#c39bd3', '#85c1e9',
  ];

  const ageCategoryChartData = {
    labels: ageCategoryTotals.map(([label]) => label),
    datasets: [{
      data: ageCategoryTotals.map(([, count]) => count),
      backgroundColor: ageCategoryTotals.map((_, i) => AGE_CATEGORY_COLORS[i % AGE_CATEGORY_COLORS.length]),
      borderWidth: 0,
    }],
  };

  const ageCategoryChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false, external: externalTooltipHandler, callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw} ${ctx.raw === 1 ? 'група' : 'груп'}` } },
    },
    cutout: '60%',
  };

  const totalGroupsCount = ageCategoryTotals.reduce((s, [, c]) => s + c, 0);

  const occupancyChartData = {
    labels: districtRows.map(r => r.district),
    datasets: [
      { label: 'Місць (факт)', data: districtRows.map(r => r.totalPlaces), backgroundColor: '#93c5fd', borderRadius: 3 },
      { label: 'Дітей',        data: districtRows.map(r => r.children),    backgroundColor: '#0069ff', borderRadius: 3 },
    ],
  };

  const occupancyChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}` } },
    },
    scales: { x: { beginAtZero: true } },
  };

  const occupancyChartHeight = Math.max(320, districtRows.length * 42);

  if (loading && !districtRows.length) return <div className="text-center py-5">Завантаження даних...</div>;
  if (error && !districtRows.length) return <div className="text-center py-5 text-danger">Помилка: {error}</div>;
  if (!districtRows.length && !loading) return <div className="text-center py-5 text-muted">Немає даних за обраними параметрами</div>;

  return (
    <>
      <div className="publicstatistic-statistics-table-section">
        <h3 className="publicstatistic-section-title">Групи в садочках</h3>
        <div className="mb-2" style={{ fontSize: '0.82rem', color: '#666' }}>
          Натисніть на «По Києву» або на район, щоб побачити розподіл по садочках
        </div>

        <div
          ref={scrollRef}
          className="publicstatistic-table-responsive"
          style={{ cursor: 'grab', overflowX: 'auto' }}
          {...dragListeners}
        >
          <table id="pubstat-groups-table" className="table publicstatistic-statistics-table">
            <thead>
              <tr>
                <th>Район</th>
                <th>Кількість груп</th>
                <th>Місць (факт)</th>
                <th>Місць (план)</th>
                <th>Дітей</th>
                <th>Киян</th>
                <th>Резерву</th>
                <th>Вільних</th>
              </tr>
            </thead>
            <tbody>
              {cityTotalRow && (
                <React.Fragment>
                  <tr
                    onClick={() => setOpenDistrict(prev => prev === CITY_ALL_KEY ? null : CITY_ALL_KEY)}
                    style={{
                      background: '#eef2ff',
                      borderBottom: '2px solid #c7d2fe',
                      cursor: 'pointer',
                      borderLeft: openDistrict === CITY_ALL_KEY ? '3px solid #0069ff' : '3px solid transparent',
                    }}
                    title="Натисніть для перегляду всіх садочків міста"
                  >
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'left', verticalAlign: 'middle', fontSize: '0.875rem' }}>
                      <span style={{ marginRight: 4, fontSize: '0.7rem', color: openDistrict === CITY_ALL_KEY ? '#0069ff' : '#6b82c4' }}>
                        {openDistrict === CITY_ALL_KEY ? '▼' : '▶'}
                      </span>
                      <strong>{cityTotalRow.district}</strong>
                    </td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{cityTotalRow.groupsCount.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{cityTotalRow.totalPlaces.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{cityTotalRow.plannedPlaces.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{cityTotalRow.children.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{cityTotalRow.kyiv.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{cityTotalRow.reserve.toLocaleString()}</strong></td>
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{cityTotalRow.free.toLocaleString()}</strong></td>
                  </tr>
                  {openDistrict === CITY_ALL_KEY && !isMobile && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, background: '#fafbff', borderLeft: '3px solid #0069ff' }}>
                        <GroupsBreakdownTable rows={allKgRows} disabilities={disabilities} showDistrict scopeLabel="місті" />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )}

              {districtRows.map((row, index) => (
                <React.Fragment key={row.district}>
                  <tr
                    className={index % 2 === 0 ? 'publicstatistic-row-even' : ''}
                    onClick={() => setOpenDistrict(prev => prev === row.district ? null : row.district)}
                    style={{
                      cursor: 'pointer',
                      borderLeft: openDistrict === row.district ? '3px solid #0069ff' : '3px solid transparent',
                      background: openDistrict === row.district ? '#f0f4ff' : undefined,
                    }}
                    title="Натисніть для перегляду садочків району"
                  >
                    <td className="publicstatistic-district-name">
                      <span style={{ marginRight: 4, fontSize: '0.7rem', color: openDistrict === row.district ? '#0069ff' : '#9ca3af' }}>
                        {openDistrict === row.district ? '▼' : '▶'}
                      </span>
                      {row.district}
                    </td>
                    <td>{row.groupsCount.toLocaleString()}</td>
                    <td>{row.totalPlaces.toLocaleString()}</td>
                    <td>{row.plannedPlaces.toLocaleString()}</td>
                    <td>{row.children.toLocaleString()}</td>
                    <td>{row.kyiv.toLocaleString()}</td>
                    <td>{row.reserve.toLocaleString()}</td>
                    <td>{row.free.toLocaleString()}</td>
                  </tr>
                  {openDistrict === row.district && !isMobile && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, background: '#fafbff', borderLeft: '3px solid #0069ff' }}>
                        <GroupsBreakdownTable rows={kgRowsByDistrict[row.district] || []} disabilities={disabilities} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {isMobile && (
          <MobileSheet
            isOpen={!!openDistrict}
            onClose={() => setOpenDistrict(null)}
            title={openDistrict === CITY_ALL_KEY ? 'Всі садочки міста' : 'Садочки району'}
            subtitle={openDistrict === CITY_ALL_KEY ? undefined : openDistrict}
          >
            <GroupsBreakdownTable
              rows={openDistrict === CITY_ALL_KEY ? allKgRows : (kgRowsByDistrict[openDistrict] || [])}
              disabilities={disabilities}
              showDistrict={openDistrict === CITY_ALL_KEY}
              scopeLabel={openDistrict === CITY_ALL_KEY ? 'місті' : 'районі'}
              capHeight={false}
            />
          </MobileSheet>
        )}
      </div>

      <div className="publicstatistic-charts-section">
        <div className="row">
          <div className="col-lg-5">
            <h4 className="publicstatistic-chart-title">Розподіл груп за віковою категорією</h4>
            <div className="publicstatistic-custom-pie-chart">
              <div className="publicstatistic-pie-chart-center">
                <span className="publicstatistic-chart-total">{totalGroupsCount.toLocaleString()}</span>
                <span className="publicstatistic-chart-total-label">всього груп</span>
              </div>
              <Pie data={ageCategoryChartData} options={ageCategoryChartOptions} />
            </div>
            <div className="publicstatistic-chart-legend publicstatistic-chart-legend-multi">
              {ageCategoryTotals.map(([label, count], index) => (
                <div key={label} className="publicstatistic-legend-item">
                  <span className="publicstatistic-legend-color" style={{ backgroundColor: AGE_CATEGORY_COLORS[index % AGE_CATEGORY_COLORS.length] }}></span>
                  <span>{label}: {count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="col-lg-7">
            <h4 className="publicstatistic-chart-title">Заповненість місць за районами</h4>
            <div style={{ position: 'relative', height: occupancyChartHeight }}>
              <Bar data={occupancyChartData} options={occupancyChartOptions} />
            </div>
            <div className="mt-2" style={{ fontSize: '0.78rem', color: '#666', textAlign: 'center' }}>
              Якщо стовпчик «Дітей» довший за «Місць (факт)» — район перевантажений
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupsTab;